import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import Button from '../../widgets/Button';
import { EventActions, EventNames } from '../../../shared/constants/analytics';
import useTracking from '../../../utilities/hooks/useTracking';
import usePageName from '../../../utilities/common/hooks/usePageName';
import CloseIcon from '../../../assets/icons/General/close.svg?react';
import NpsRatingStep from './components/NpsRatingStep';
import NpsThankYouStep from './components/NpsThankYouStep';
import NpsInputTextStep from './components/NpsInputTextStep';

type Props = {
  cooldownPeriodElapsed?: boolean;
};

enum NpsStep {
  Rating = 'RATING',
  Feedback = 'FEEDBACK',
  ThankYou = 'THANK_YOU',
}

export default function NpsSurvey({ cooldownPeriodElapsed = false }: Props) {
  const [formState, setFormState] = useState<{
    rating: number;
    showSurvey: boolean;
  }>({
    rating: 0,
    showSurvey: cooldownPeriodElapsed,
  });

  const [currentStep, setCurrentStep] = useState<NpsStep>(NpsStep.Rating);

  const { rating, showSurvey } = formState;
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { pageName } = usePageName();

  const handleRatingClick = (value: number) => {
    setFormState((prev) => ({ ...prev, rating: value }));
    setCurrentStep(NpsStep.Feedback);
  };

  const renderStep = React.useMemo(() => {
    switch (currentStep) {
      case NpsStep.Rating:
        return <NpsRatingStep onRatingSelect={handleRatingClick} />;

      case NpsStep.Feedback:
        return (
          <NpsInputTextStep
            ratingScore={rating}
            onSubmit={() => {
              trackEvent(EventNames.WellBeingDashboard, {
                eventLabel: 'feedback_rating_submit',
                eventAction: EventActions.click,
                eventCategory: 'feedback_rating',
                eventValue: rating.toString(),
                eventSource: pageName,
              });
              setCurrentStep(NpsStep.ThankYou);
            }}
          />
        );

      case NpsStep.ThankYou:
        return <NpsThankYouStep />;

      default:
        return null;
    }
  }, [currentStep, t, rating]);

  return (
    <div
      className={`fixed right-0 bottom-0 left-0 z-50 flex w-full justify-center bg-white px-4 py-6 shadow-[0_4px_12px_0_#10182824] transition-transform duration-700 ${
        showSurvey ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <Button
        variant="ghost"
        className="absolute top-2 right-2"
        onClick={() => {
          setFormState((prev) => ({ ...prev, showSurvey: false }));
          if (currentStep !== NpsStep.ThankYou) {
            trackEvent(EventNames.WellBeingDashboard, {
              eventLabel: 'feedback_rating_close',
              eventAction: EventActions.click,
              eventCategory: 'feedback_rating',
              eventSource: pageName,
            });
          }
        }}
      >
        <CloseIcon className="stroke-neutral-700" />
      </Button>
      {renderStep}
    </div>
  );
}
